import React, { useState } from 'react';
import { 
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  PlusIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CalculatorIcon,
  DocumentChartBarIcon,
  CogIcon,
  ExclamationCircleIcon,
  RocketLaunchIcon,
  ComputerDesktopIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';

const Help = () => {
  const [expandedSections, setExpandedSections] = useState(new Set(['getting-started', 'sla-comprehensive']));

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const helpSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: QuestionMarkCircleIcon,
      content: [
        {
          title: 'Welcome to Alula IT Helpdesk',
          content: 'The Alula IT Helpdesk is a comprehensive ticketing system designed to streamline IT support operations. This system allows users to submit support requests, track their progress, and enables technicians to efficiently manage and resolve issues.',
          type: 'info'
        },
        {
          title: 'System Overview',
          content: 'The helpdesk consists of several key areas: Dashboard for overview, Ticket creation and management, Technician view for support staff, Reports for analytics, Settings for configuration, and this Help section for guidance.',
          type: 'info'
        },
        {
          title: 'User Roles',
          content: 'There are three main user roles: Users (can create and view their own tickets), Technicians (can manage all tickets and provide support), and Administrators (have full system access including settings and user management).',
          type: 'info'
        }
      ]
    },
    {
      id: 'creating-tickets',
      title: 'Creating Tickets',
      icon: PlusIcon,
      content: [
        {
          title: 'How to Submit a New Ticket',
          content: 'Navigate to "New Ticket" in the sidebar. Fill in the required fields: Title (brief description), Description (detailed explanation), Category (select the most appropriate), and Priority (Low, Medium, High, or Critical). Click "Submit Ticket" to create your request.',
          type: 'step'
        },
        {
          title: 'Ticket Priority Guidelines',
          content: 'Low: General questions or minor issues (72-hour SLA). Medium: Standard support requests (24-hour SLA). High: Urgent issues affecting work (8-hour SLA). Critical: System outages or security issues requiring immediate attention (4-hour SLA).',
          type: 'guideline'
        },
        {
          title: 'Best Practices for Ticket Creation',
          content: 'Provide clear, detailed descriptions. Include error messages if applicable. Specify what you were doing when the issue occurred. Mention any steps you\'ve already tried to resolve the issue.',
          type: 'tip'
        }
      ]
    },
    {
      id: 'ticket-statuses',
      title: 'Understanding Ticket Statuses',
      icon: ClockIcon,
      content: [
        {
          title: 'New',
          content: 'Ticket has been created and is awaiting assignment to a technician.',
          type: 'status',
          icon: ClockIcon,
          color: 'text-blue-500'
        },
        {
          title: 'In Progress',
          content: 'A technician is actively working on resolving the issue.',
          type: 'status',
          icon: ExclamationTriangleIcon,
          color: 'text-yellow-500'
        },
        {
          title: 'Waiting Feedback',
          content: 'Technician has provided a solution and is waiting for your confirmation or feedback.',
          type: 'status',
          icon: ClockIcon,
          color: 'text-orange-500'
        },
        {
          title: 'Closed',
          content: 'Issue has been resolved and the ticket is closed.',
          type: 'status',
          icon: CheckCircleIcon,
          color: 'text-green-500'
        },
        {
          title: 'Reopened',
          content: 'A closed ticket has been reopened due to recurring issues or additional problems.',
          type: 'status',
          icon: ExclamationTriangleIcon,
          color: 'text-red-500'
        }
      ]
    },
    {
      id: 'dashboard-navigation',
      title: 'Dashboard & Navigation',
      icon: UserIcon,
      content: [
        {
          title: 'Dashboard Overview',
          content: 'The dashboard provides a comprehensive view of your tickets. You can see statistics including total tickets, new tickets, in-progress tickets, waiting feedback, closed tickets, reopened tickets, and SLA breaches.',
          type: 'info'
        },
        {
          title: 'Filtering Tickets',
          content: 'Use the status and category filters to find specific tickets. You can filter by any ticket status or category to narrow down your view.',
          type: 'tip'
        },
        {
          title: 'Ticket Details',
          content: 'Click on any ticket title to view detailed information including description, responses, root cause analysis, and solution details.',
          type: 'info'
        }
      ]
    },
    {
      id: 'technician-view',
      title: 'Technician View',
      icon: WrenchScrewdriverIcon,
      content: [
        {
          title: 'Accessing Technician View',
          content: 'Technicians can access the "Technician View" from the sidebar to see all tickets in the system, not just their own.',
          type: 'info'
        },
        {
          title: 'Updating Tickets',
          content: 'Technicians can update ticket status, assign themselves or other technicians, provide responses, document root causes, and add solution details.',
          type: 'step'
        },
        {
          title: 'SLA Management',
          content: 'The system automatically tracks SLA compliance. Technicians should ensure tickets are resolved within the specified timeframes based on priority levels. Monitor the SLA indicators (green checkmarks for meeting SLA, red X for breaches) to prioritize work effectively.',
          type: 'guideline'
        }
      ]
    },
    {
      id: 'sla-comprehensive',
      title: 'Service Level Agreements (SLA)',
      icon: CalculatorIcon,
      content: [
        {
          title: 'What is SLA?',
          content: 'Service Level Agreements (SLA) define the expected response and resolution times for support tickets based on their priority level. SLAs help ensure consistent service quality and set clear expectations for both users and technicians.',
          type: 'info'
        },
        {
          title: 'SLA Compliance Definition',
          content: 'SLA Compliance is the percentage of tickets that are meeting their SLA targets compared to the total number of tickets that should be evaluated for SLA compliance. This includes both closed tickets resolved on time and open tickets within SLA.',
          type: 'info'
        },
        {
          title: 'What Counts as "Meeting SLA"',
          content: '• Closed tickets resolved on time (before or at SLA target)\n• Open tickets within SLA (haven\'t exceeded target yet)',
          type: 'guideline'
        },
        {
          title: 'What Counts as "Breaching SLA"',
          content: '• Closed tickets resolved late (after SLA target)\n• Open tickets exceeding SLA (current time > SLA target)',
          type: 'guideline'
        },
        {
          title: 'Compliance Rate Formula',
          content: 'Compliance Rate = (Tickets Meeting SLA) / (Total Tickets for Compliance) × 100%\n\nWhere:\n• Tickets Meeting SLA = Closed tickets resolved on time + Open tickets within SLA\n• Total Tickets for Compliance = Closed tickets with SLA data + Open tickets that have breached SLA',
          type: 'step'
        },
        {
          title: 'Example Calculation',
          content: 'If you have 3 tickets meeting SLA and 1 ticket breaching SLA:\nCompliance Rate = 3 / 4 = 75%\n\nThis gives you a realistic view of current SLA performance.',
          type: 'tip'
        }
      ]
    },
    {
      id: 'sla-configuration',
      title: 'SLA Configuration & Setup',
      icon: CogIcon,
      content: [
        {
          title: 'Accessing SLA Configuration',
          content: 'Navigate to Settings → SLA Configuration. Only administrators can access and modify SLA settings.',
          type: 'step'
        },
        {
          title: 'Setting Up SLA Rules',
          content: 'For each priority level (Critical, High, Medium, Low), configure:\n• Response Time: How quickly a technician should acknowledge the ticket\n• Resolution Time: How quickly the ticket should be resolved\n• Description: Optional notes about the SLA rule',
          type: 'step'
        },
        {
          title: 'Recommended SLA Timeframes',
          content: 'Critical: 30 min response, 4 hours resolution\nHigh: 2 hours response, 8 hours resolution\nMedium: 4 hours response, 24 hours resolution\nLow: 8 hours response, 72 hours resolution\n\nAdjust these based on your organization\'s needs.',
          type: 'guideline'
        },
        {
          title: 'Activating SLA Rules',
          content: 'Ensure the "Is Active" checkbox is checked for each SLA rule you want to enforce. Inactive rules won\'t be used in compliance calculations.',
          type: 'tip'
        },
        {
          title: 'Testing SLA Configuration',
          content: 'After setting up SLA rules, create test tickets with different priorities to verify that SLA targets are being calculated correctly.',
          type: 'tip'
        }
      ]
    },
    {
      id: 'sla-reports',
      title: 'SLA Reports & Analytics',
      icon: DocumentChartBarIcon,
      content: [
        {
          title: 'Accessing SLA Reports',
          content: 'Navigate to Reports → SLA Report to view comprehensive SLA performance metrics and analytics.',
          type: 'step'
        },
        {
          title: 'SLA Report Components',
          content: 'The SLA report includes:\n• Summary cards showing total tickets, met SLA, breached SLA, and compliance rate\n• Compliance calculation breakdown\n• Charts showing performance by priority\n• Detailed metrics table with resolution times\n• Export functionality for Excel reports',
          type: 'info'
        },
        {
          title: 'Understanding Summary Metrics',
          content: '• Total Tickets: All tickets in the system\n• Met SLA: Tickets meeting their SLA targets\n• Breached SLA: Tickets exceeding their SLA targets\n• Compliance Rate: Percentage of tickets meeting SLA\n• In Progress (Within SLA): Open tickets still within target\n• Closed (No SLA Data): Closed tickets without SLA targets',
          type: 'info'
        },
        {
          title: 'Priority-Level Analysis',
          content: 'Each priority level shows:\n• Total tickets for that priority\n• Met SLA count and percentage\n• Breached SLA count\n• Expected vs actual resolution times\n• Performance indicators (meeting/breaching targets)',
          type: 'info'
        },
        {
          title: 'Exporting SLA Data',
          content: 'Use the "Export Excel" button to download detailed SLA reports for further analysis or sharing with stakeholders.',
          type: 'tip'
        },
        {
          title: 'Interpreting SLA Trends',
          content: 'Monitor compliance rates over time to identify:\n• Areas needing process improvement\n• Priority levels with consistent breaches\n• Seasonal patterns in ticket volume\n• Technician performance trends',
          type: 'tip'
        }
      ]
    },
    {
      id: 'reports-analytics',
      title: 'Reports & Analytics',
      icon: ChartBarIcon,
      content: [
        {
          title: 'SLA Reports',
          content: 'View Service Level Agreement performance metrics including compliance rates, response times, and resolution times by priority level.',
          type: 'info'
        },
        {
          title: 'Ticket Churn Report',
          content: 'Analyze ticket creation and resolution patterns over time. Select date ranges to view daily activity, priority breakdowns, and status distributions.',
          type: 'info'
        },
        {
          title: 'Understanding Metrics',
          content: 'SLA compliance rate shows the percentage of tickets resolved within target times. Average resolution time helps identify areas for process improvement.',
          type: 'tip'
        }
      ]
    },
    {
      id: 'settings-configuration',
      title: 'Settings & Configuration',
      icon: Cog6ToothIcon,
      content: [
        {
          title: 'SLA Configuration',
          content: 'Administrators can configure SLA rules by priority level, setting response and resolution time targets. These rules determine SLA compliance calculations. Access this through Settings → SLA Configuration to set up timeframes for each priority level.',
          type: 'info'
        },
        {
          title: 'User Management',
          content: 'Manage user accounts, assign roles (User, Technician, Administrator), and update user information as needed.',
          type: 'info'
        },
        {
          title: 'Email Configuration',
          content: 'Administrators manage category notification email templates under Settings → Email Configuration. Send a test email where SendGrid is enabled (Staging redirects to the test inbox) to verify content — there is no in-app preview of rendered HTML.',
          type: 'info'
        },
        {
          title: 'System Information',
          content: 'At the bottom of Settings, System Information shows application version, environment (for example Development), and the database you are connected to. Locally this is usually SQL Server LocalDB; staging and production show Azure SQL. Connection and backup labels match that host — they are not fixed Azure text when you run on your machine.',
          type: 'info'
        }
      ]
    },
    {
      id: 'asset-management',
      title: 'Asset Management',
      icon: ComputerDesktopIcon,
      content: [
        {
          title: 'Asset Management Overview',
          content: 'The Asset Management system allows administrators to track laptop inventory, manage asset assignments, and maintain comprehensive records of IT equipment. This feature is available to administrators only.',
          type: 'info'
        },
        {
          title: 'Accessing Asset Management',
          content: 'Navigate to Settings → Asset Management in the sidebar. This section is only visible to users with Administrator role.',
          type: 'step'
        },
        {
          title: 'Asset Registration',
          content: 'To register a new laptop asset:\n• Click "Add Asset" button\n• Fill in required fields: Asset Name, Type Code, Asset Tag, Assigned To, Status, Purchase Date, Serial Number, Model\n• Optional fields: Location, Notes, Invoice\n• Click "Add Asset" to save',
          type: 'step'
        },
        {
          title: 'Asset Tag Generation',
          content: 'Asset tags follow the format ALU-LT-YY-XXXX where:\n• ALU = Company prefix\n• LT = Laptop Type code\n• YY = Current year (last 2 digits)\n• XXXX = Random 4-digit number\n\nUse the "Generate" button next to the Asset Tag field to auto-generate a unique tag.',
          type: 'guideline'
        },
        {
          title: 'Asset Status Management',
          content: 'Assets can have the following statuses:\n• In Use: Currently assigned and being used\n• Retired: No longer in service\n• Lost: Asset has been lost or stolen\n• Damaged: Asset is damaged and needs repair\n• Under Repair: Asset is being repaired',
          type: 'info'
        },
        {
          title: 'Searching and Filtering Assets',
          content: 'Use the search bar to find assets by:\n• Assigned person name\n• Asset tag\n• Serial number\n\nUse the Status Filter dropdown to view assets by their current status.',
          type: 'tip'
        },
        {
          title: 'Editing Assets',
          content: 'To edit an existing asset:\n• Click the edit (pencil) icon next to the asset\n• Modify the required information\n• Ensure unique constraints (Asset Tag and Serial Number)\n• Click "Update Asset" to save changes',
          type: 'step'
        },
        {
          title: 'Deleting Assets',
          content: 'To delete an asset:\n• Click the delete (trash) icon next to the asset\n• Confirm the deletion in the popup dialog\n• The asset will be permanently removed from the system',
          type: 'step'
        },
        {
          title: 'Asset Data Fields',
          content: 'Required Fields:\n• Asset Name: Descriptive name (e.g., "Laptop")\n• Type Code: Asset type identifier (e.g., "LT" for Laptop)\n• Asset Tag: Unique identifier (auto-generated)\n• Assigned To: Employee name\n• Status: Current asset status\n• Purchase Date: When the asset was purchased\n• Serial Number: Hardware serial number\n• Model: Device model (e.g., "Lenovo ThinkPad E16 G3")\n\nOptional Fields:\n• Location: Physical location of the asset\n• Notes: Additional information\n• Invoice: Invoice reference or file name',
          type: 'info'
        },
        {
          title: 'Best Practices',
          content: '• Always verify serial numbers before adding assets\n• Keep asset tags unique and consistent\n• Update asset status promptly when changes occur\n• Include invoice references for audit purposes\n• Regularly review and update asset assignments',
          type: 'tip'
        }
      ]
    },
    {
      id: 'supplier-management',
      title: 'Supplier Management',
      icon: BuildingStorefrontIcon,
      content: [
        {
          title: 'Supplier Management Overview',
          content: 'The Supplier Management module allows administrators to maintain a central register of IT suppliers, track service contracts, and manage invoices with file attachments. This feature is available to administrators only.',
          type: 'info'
        },
        {
          title: 'Accessing Supplier Management',
          content: 'Navigate to Suppliers in the sidebar. This section is only visible to users with the Administrator role.',
          type: 'step'
        },
        {
          title: 'Adding a Supplier',
          content: 'To add a new supplier:\n• Click "Add Supplier"\n• Enter the supplier Name (required)\n• Optionally add Contact Name, Contact Email, Contact Phone, Website, and Notes\n• Click "Save" to create the supplier',
          type: 'step'
        },
        {
          title: 'Managing Contracts',
          content: 'Each supplier can have multiple service contracts. To add a contract:\n• Select the supplier from the list\n• Go to the Contracts tab\n• Click "Add Contract"\n• Fill in: Service Name (required), Description, Cost, Currency (default ZAR), Billing Cycle (Monthly/Quarterly/Annual/One-Off), Contract Start/End Dates, Auto-Renew toggle, Status, and Notes\n• Click "Save"',
          type: 'step'
        },
        {
          title: 'Contract Statuses',
          content: '• Active: Contract is currently in force\n• Pending: Contract not yet started\n• Expired: Contract end date has passed\n• Cancelled: Contract was terminated early',
          type: 'info'
        },
        {
          title: 'Managing Invoices',
          content: 'Each supplier can have multiple invoices, optionally linked to a specific contract. To add an invoice:\n• Select the supplier, then go to the Invoices tab\n• Click "Add Invoice"\n• Fill in: Invoice Number, Invoice Date, Due Date, Amount (required), Currency, Status, and Notes\n• Optionally link the invoice to a contract via the Contract dropdown\n• Attach a file (PDF, images, or Office documents) at creation time\n• Click "Save"',
          type: 'step'
        },
        {
          title: 'Invoice Statuses',
          content: '• Pending: Invoice received but not yet paid\n• Paid: Invoice has been settled\n• Overdue: Payment due date has passed without payment',
          type: 'info'
        },
        {
          title: 'Invoice File Attachments',
          content: 'You can attach a supporting file (e.g. PDF invoice) when creating an invoice. Accepted formats: PDF, images (JPG, PNG), and Office documents. Once attached, a download link appears in the invoice row. Note: files can only be attached at creation — editing an existing invoice does not replace the file.',
          type: 'guideline'
        },
        {
          title: 'Editing and Deleting Records',
          content: 'Use the pencil icon to edit any supplier, contract, or invoice. Use the trash icon to delete. Deleting a supplier will also permanently remove all its contracts and invoices — confirm the deletion dialog carefully.',
          type: 'tip'
        },
        {
          title: 'Best Practices',
          content: '• Keep one supplier record per vendor — avoid duplicates\n• Link invoices to their corresponding contract where possible for better tracking\n• Update contract status promptly when contracts expire or are cancelled\n• Attach invoice PDFs at creation time for a complete audit trail\n• Use the Notes field on suppliers to record account numbers or special instructions',
          type: 'tip'
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: ExclamationTriangleIcon,
      content: [
        {
          title: 'Common Issues',
          content: 'If you experience login issues, try refreshing the page or clearing your browser cache. For ticket creation problems, ensure all required fields are completed.',
          type: 'tip'
        },
        {
          title: 'SLA Breach Indicators',
          content: 'Red X icons indicate SLA breaches. These appear when tickets exceed their target resolution times and are not in Closed or Reopened status. Green checkmarks indicate tickets meeting their SLA targets. Monitor these indicators to prioritize urgent tickets.',
          type: 'info'
        },
        {
          title: 'Getting Additional Help',
          content: 'If you continue to experience issues, contact your system administrator or IT support team for assistance.',
          type: 'contact'
        }
      ]
    },
    {
      id: 'sla-troubleshooting',
      title: 'SLA Troubleshooting',
      icon: ExclamationCircleIcon,
      content: [
        {
          title: 'SLA Not Calculating Correctly',
          content: 'If SLA compliance rates seem incorrect, check:\n• SLA rules are properly configured and active\n• Ticket priorities are set correctly\n• System time is accurate\n• Tickets have proper creation and resolution timestamps',
          type: 'tip'
        },
        {
          title: 'Tickets Not Showing SLA Targets',
          content: 'Ensure that:\n• SLA rules are configured for all priority levels\n• Rules are marked as "Active"\n• Tickets have valid priority assignments\n• System has been restarted after SLA configuration changes',
          type: 'tip'
        },
        {
          title: 'Compliance Rate Showing 100% When It Shouldn\'t',
          content: 'This usually indicates that only closed tickets are being considered. The system now includes both closed and open tickets in compliance calculations. Check that open tickets with breached SLA are being counted properly.',
          type: 'info'
        },
        {
          title: 'SLA Reports Not Updating',
          content: 'Try refreshing the SLA report page. If issues persist, check that:\n• Database connections are working\n• SLA service is running properly\n• No system maintenance is in progress',
          type: 'tip'
        },
        {
          title: 'Exporting SLA Data Issues',
          content: 'If Excel export fails:\n• Check browser permissions for downloads\n• Ensure sufficient disk space\n• Try using a different browser\n• Contact administrator if issue persists',
          type: 'tip'
        }
      ]
    },
    {
      id: 'release-notes',
      title: 'Release Notes',
      icon: RocketLaunchIcon,
      content: [
        {
          title: 'Version 2025.08.25 - Enhanced Reporting & Category Management',
          content: 'This major release introduces significant enhancements to the Alula IT Helpdesk system, focusing on improved ticket management, enhanced reporting capabilities, and better user experience.',
          type: 'info'
        },
        {
          title: '🎯 New Features',
          content: '• Category Change Functionality: Users can now change ticket categories directly from the ticket detail view\n• Enhanced Reporting System: Executive Summary, Ticket Category Breakdown, and improved SLA reports\n• Multi-Select Filtering: Advanced filtering system for tickets by status and category\n• Asset Management System: Complete asset management for tracking laptop inventory with unique asset tags and serial numbers\n• UI/UX Improvements: Better login button design, cleaner charts, and enhanced user experience',
          type: 'step'
        },
        {
          title: '🔧 Technical Improvements',
          content: '• Security Enhancements: App Service Application Settings for secrets; Managed Identity for blob and Graph\n• Database Improvements: New CategoryId column in TicketResponses table and Assets table for inventory tracking\n• Backend Enhancements: Improved API error handling and service layer\n• Asset Management API: New controller and service layer for asset management\n• Performance Optimizations: Enhanced database queries and caching',
          type: 'step'
        },
        {
          title: '🐛 Bug Fixes',
          content: '• Ticket Management: Fixed 400 errors when loading ticket responses\n• SLA Calculations: Corrected breach rate calculation logic\n• UI Components: Fixed chart rendering and form validation issues\n• Field Name Mapping: Resolved PascalCase/camelCase mismatches',
          type: 'tip'
        },
        {
          title: '⚠️ Breaking Changes',
          content: '• Database Schema: New CategoryId column added to TicketResponses table and Assets table for inventory tracking\n• API Changes: Enhanced ticket response DTOs with additional fields and new asset management endpoints\n• Migration Required: Manual SQL script execution needed for production deployment',
          type: 'guideline'
        },
        {
          title: '📊 Enhanced Reports',
          content: '• Executive Summary Report: Comprehensive dashboard combining SLA, churn, and category data\n• Ticket Category Breakdown: Visual pie charts showing ticket distribution\n• Improved SLA Reports: Enhanced compliance calculations and metrics\n• Export Capabilities: Excel export for all reports',
          type: 'info'
        },
        {
          title: '🔍 Multi-Select Filtering',
          content: '• Advanced filtering system for tickets by status and category\n• Real-time filtering without page refresh\n• Persistent filter state across sessions\n• Applied to Dashboard and Technician View pages',
          type: 'info'
        },
        {
          title: '💻 Asset Management System',
          content: '• Complete asset management for tracking laptop inventory\n• Asset registration with unique asset tags and serial numbers\n• Assignment tracking to employees with status management\n• Purchase date and invoice tracking\n• Search and filter capabilities for asset management\n• Role-based access control (Administrators only)',
          type: 'info'
        },
        {
          title: '🏪 Supplier Management',
          content: '• Central supplier register with contact information\n• Service contract tracking per supplier (billing cycle, cost, auto-renew, status)\n• Invoice management with optional contract linkage\n• File attachment support for invoice documents (PDF, images, Office docs)\n• Invoice download directly from the invoices list\n• Role-based access control (Administrators only)',
          type: 'info'
        },
        {
          title: '🔐 Security Enhancements',
          content: '• Configuration: App Service Application Settings via ADO pipeline variable replacement (SQL, SendGrid)\n• Environment-Specific Configuration: Development / Staging / Production appsettings\n• Blob storage: one ConnectionStrings:AzureStorage value — Azurite locally, blob endpoint URL + Managed Identity in Azure\n• Managed Identity Support: Secure authentication for Azure Blob and Microsoft Graph\n• Outbound email: non-Production TestEmailRedirect to a test inbox; Production delivers to real recipients\n• Secret Management: Prefer App Service Application Settings for secrets',
          type: 'info'
        },
        {
          title: '📦 Dependencies & Requirements',
          content: 'Backend: .NET 10, Entity Framework Core, Azure SDK\nFrontend: React 18, Recharts, TypeScript where present\nAzure Services: SQL Database, App Service, Blob Storage',
          type: 'info'
        },
        {
          title: '🚀 Deployment Notes',
          content: '• Automatic: Entity Framework migrations will apply automatically\n• Manual: Execute Add-CategoryId-To-TicketResponse.sql script against production database\n• Configuration: Environment-specific appsettings and App Service Application Settings\n• Testing: Comprehensive testing recommended for all new features',
          type: 'step'
        },
        {
          title: '📖 Detailed Documentation',
          content: 'For complete technical details, implementation guides, and troubleshooting information, refer to the comprehensive documentation available in the project repository.',
          type: 'contact'
        }
      ]
    }
  ];

  const getContentIcon = (type, icon, color) => {
    if (type === 'status' && icon) {
      const IconComponent = icon;
      return <IconComponent className={`h-5 w-5 ${color} mr-2`} />;
    }
    return null;
  };

  const getContentStyle = (type) => {
    switch (type) {
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'tip':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'guideline':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'step':
        return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'status':
        return 'bg-gray-50 border-gray-200 text-gray-800';
      case 'contact':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'sla':
        return 'bg-indigo-50 border-indigo-200 text-indigo-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Help & Knowledge Base</h1>
        <p className="text-gray-600">Learn how to use the Alula IT Helpdesk system effectively</p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <QuestionMarkCircleIcon className="h-8 w-8 text-blue-500" />
            <div>
              <h2 className="text-lg font-medium text-gray-900">Quick Navigation</h2>
              <p className="text-sm text-gray-600">Click on any section to expand and view detailed information</p>
            </div>
          </div>

          <div className="space-y-4">
            {helpSections.map((section) => {
              const IconComponent = section.icon;
              const isExpanded = expandedSections.has(section.id);
              
              return (
                <div key={section.id} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                  >
                    <div className="flex items-center space-x-3">
                      <IconComponent className="h-5 w-5 text-gray-500" />
                      <span className="font-medium text-gray-900">{section.title}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-4">
                      {section.content.map((item, index) => (
                        <div key={index} className={`border rounded-lg p-4 ${getContentStyle(item.type)}`}>
                          <div className="flex items-start">
                            {getContentIcon(item.type, item.icon, item.color)}
                            <div className="flex-1">
                              <h4 className="font-medium mb-2">{item.title}</h4>
                              <div className="text-sm leading-relaxed whitespace-pre-line">{item.content}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <DocumentTextIcon className="h-6 w-6 text-blue-500 mt-1" />
          <div>
            <h3 className="text-lg font-medium text-blue-900 mb-2">Need More Help?</h3>
            <p className="text-blue-800 mb-4">
              If you need additional assistance or have questions not covered in this knowledge base, 
              please contact your system administrator or IT support team.
            </p>
            <div className="text-sm text-blue-700">
              <p><strong>System Version:</strong> 2025.08.25</p>
              <p><strong>Last Updated:</strong> {new Date().toLocaleDateString()}</p>
              <p><strong>Release Notes:</strong> Available in the Release Notes section above</p>
              <p><strong>SLA Documentation:</strong> Comprehensive SLA guide included</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help; 